/**
 * PATH: apps/qa/src/pages/EvidencePage.tsx
 * PURPOSE: Display and manage QA evidence (photos, documents) across inspections
 * 
 * WHY: Users need a central location to view, upload, and manage evidence files
 * that are attached to inspections and areas.
 * 
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ Fetch       │───▶│ Filter by    │───▶│ Display     │
 *   │ Evidence    │    │ Inspection   │    │ Evidence    │
 *   └─────────────┘    └──────────────┘    └─────────────┘
 * 
 * DEPENDENCIES:
 *   - @quotech/api: API client
 *   - @tanstack/react-query: Data fetching
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@quotech/auth'
import api from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  Camera,
  Upload,
  Search,
  Filter,
  Image,
  FileText,
  Video,
  Download,
  Eye,
  Trash2,
  Plus,
  Loader2,
  Calendar,
  MapPin,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react'

interface Evidence {
  id: string
  evidence_type: 'photo' | 'document' | 'video'
  caption: string
  asset_url?: string
  asset_id?: string
  inspection_id?: string
  inspection_name?: string
  area_id?: string
  area_name?: string
  floor_name?: string
  ai_quality_score?: number
  ai_quality_warnings?: string[]
  created_at: string
  created_by_name?: string
}

export default function EvidencePage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterInspection, setFilterInspection] = useState<string>('')

  // Wait for org context before fetching
  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  // Fetch evidence from API
  const { data: evidenceData, isLoading, error } = useQuery({
    queryKey: ['qa-evidence', filterType, filterInspection, organisationId],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (filterType !== 'all') params.evidence_type = filterType
      if (filterInspection) params.inspection_id = filterInspection
      const response = await api().get('/qa/evidence/', { params })
      // WHY: Backend may return plain array or paginated { results, count }
      const raw = response.data
      if (Array.isArray(raw)) return { results: raw, count: raw.length }
      if (raw && Array.isArray(raw.results)) return raw
      return { results: [], count: 0 }
    },
    enabled: isAuthReady,
  })

  const showLoading = isLoading || !isAuthReady
  const evidence: Evidence[] = Array.isArray(evidenceData?.results) ? evidenceData.results : []

  // Filter by search query
  const filteredEvidence = evidence.filter(e =>
    e.caption?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.area_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.inspection_name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'photo': return <Image className="w-5 h-5" />
      case 'video': return <Video className="w-5 h-5" />
      case 'document': return <FileText className="w-5 h-5" />
      default: return <Camera className="w-5 h-5" />
    }
  }

  const getQualityBadge = (score?: number, warnings?: string[]) => {
    if (score === undefined) return null
    if (score >= 80) {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          <CheckCircle className="w-3 h-3" />
          Good Quality
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        <AlertTriangle className="w-3 h-3" />
        {warnings?.length ? warnings[0] : 'Quality Issues'}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Evidence</h1>
          <p className="text-muted-foreground">
            Photos, documents, and videos attached to inspections
          </p>
        </div>
        <Link
          to="/evidence/upload"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Upload Evidence
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search evidence..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            <option value="all">All Types</option>
            <option value="photo">Photos</option>
            <option value="document">Documents</option>
            <option value="video">Videos</option>
          </select>
        </div>
      </div>

      {/* Evidence Grid */}
      {showLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <p className="text-foreground font-medium">Failed to load evidence</p>
          <p className="text-sm text-muted-foreground mt-1">Please try again later</p>
        </div>
      ) : filteredEvidence.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
            <Camera className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-foreground font-medium">No evidence found</p>
          <p className="text-sm text-muted-foreground mt-1">
            {searchQuery ? 'Try adjusting your search or filters' : 'Upload your first evidence to get started'}
          </p>
          <Link
            to="/evidence/upload"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Upload Evidence
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredEvidence.map((item) => (
            <div
              key={item.id}
              className="group bg-card rounded-xl shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video bg-muted">
                {item.asset_url ? (
                  <img
                    src={item.asset_url}
                    alt={item.caption}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    {getTypeIcon(item.evidence_type)}
                  </div>
                )}
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
                {/* Type badge */}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-black/50 text-white capitalize">
                  {getTypeIcon(item.evidence_type)}
                  {item.evidence_type}
                </span>
              </div>

              {/* Content */}
              <div className="p-4">
                <p className="font-medium text-foreground line-clamp-1">
                  {item.caption || 'Untitled'}
                </p>
                
                {(item.inspection_name || item.area_name) && (
                  <div className="mt-2 space-y-1">
                    {item.inspection_name && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {item.inspection_name}
                      </p>
                    )}
                    {item.area_name && (
                      <p className="text-xs text-muted-foreground">
                        {item.floor_name && `${item.floor_name} › `}{item.area_name}
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(item.created_at).toLocaleDateString()}
                  </p>
                  {getQualityBadge(item.ai_quality_score, item.ai_quality_warnings)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

